<template>
  <div class="wrestler-page">
    <div v-if="wrestler">
      <div class="wrestler-content">
        <div class="left-column">
          <div class="profile-card">
            <div class="profile-head">
              <div class="avatar-ring">
                <img
                  v-if="
                    authenticated && wrestler.Image && wrestler.Image !== ''
                  "
                  :src="wrestler.Image"
                  class="profile-avatar"
                  alt=""
                />
                <img
                  v-else
                  :src="require('../assets/default-profile.png')"
                  class="profile-avatar"
                  alt=""
                />
              </div>
              <div class="profile-meta">
                <h1 class="wrestler-name">
                  {{ wrestler.FirstName }} {{ wrestler.LastName }}
                </h1>
                <div class="profile-stats">
                  <div class="record-row">
                    <span class="record-badge"
                      >{{ displayWins }}–{{ displayLosses }}</span
                    >
                    <span class="record-label">Career</span>
                  </div>
                  <span class="record-label"
                    >Season
                    {{
                      selectedSeason && selectedSeason !== "All"
                        ? selectedSeason
                        : currentSeasonLabel
                    }}</span
                  >
                </div>
                <div class="skill-bar-row">
                  <div class="skill-bar skill-bar--linear">
                    <div class="skill-bar-linear-track">
                      <div
                        class="skill-bar-linear-fill"
                        :style="{ width: skillBarLinearWidthPercent + '%' }"
                      ></div>
                    </div>
                    <span class="skill-bar-label">
                      {{
                        formatSkillLevel(
                          wrestler && wrestler.Skill != null
                            ? wrestler.Skill
                            : 0
                        )
                      }}
                    </span>
                  </div>
                  <v-tooltip right max-width="320">
                    <template v-slot:activator="{ on }">
                      <v-icon small class="skill-info-icon" v-on="on"
                        >info</v-icon
                      >
                    </template>
                    <span
                      >ELO-style rating. The bar fills from the
                      <strong>lowest</strong> to the
                      <strong>highest</strong> skill in your league ({{
                        leagueSkillBarScaleHint
                      }}). Your number is the exact rating.</span
                    >
                  </v-tooltip>
                </div>
                <div
                  v-if="skillTrendPoints.length > 0"
                  class="skill-sparkline-wrap"
                >
                  <span class="skill-sparkline-label">Skill trend</span>
                  <svg
                    class="skill-sparkline"
                    viewBox="0 0 120 28"
                    preserveAspectRatio="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      :d="skillTrendPath"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div
                  v-if="wrestler.RankLeague || wrestler.RankWeightClass"
                  class="rank-in-weight-section"
                >
                  <span class="detail-label">Rank in weight class</span>
                  <div class="rank-in-weight-value">
                    <template v-if="weightRank != null">
                      <span class="rank-in-weight-text">
                        #{{ weightRank }} of {{ weightCount }}
                      </span>
                      <span
                        v-if="
                          rankChangeFromPrevious != null &&
                          rankChangeFromPrevious !== 0
                        "
                        class="rank-change-pill"
                        :class="
                          rankChangeFromPrevious > 0
                            ? 'rank-change-up'
                            : 'rank-change-down'
                        "
                      >
                        {{ rankChangeFromPrevious > 0 ? "↑" : "↓"
                        }}{{
                          rankChangeFromPrevious > 0
                            ? rankChangeFromPrevious
                            : -rankChangeFromPrevious
                        }}
                      </span>
                    </template>
                    <span v-else class="rank-in-weight-unset">
                      {{
                        wrestler.RankLeague && wrestler.RankWeightClass
                          ? "Not ranked in this weight class"
                          : "Set league and weight to see rank"
                      }}
                    </span>
                  </div>
                </div>
                <div class="detail-grid">
                  <span class="detail-label">Grade</span>
                  <span class="detail-value">{{ wrestler.Grade }}</span>
                  <span class="detail-label">School</span>
                  <span class="detail-value">{{ wrestler.School }}</span>
                  <span class="detail-label">League</span>
                  <span class="detail-value">{{ wrestler.RankLeague }}</span>
                  <span class="detail-label">Weight</span>
                  <span class="detail-value">{{
                    wrestler.RankWeightClass
                  }}</span>
                </div>
              </div>
            </div>

            <div v-if="authenticated" class="audit-section">
              <v-card>
                <v-list dense>
                  <v-subheader>Profile Change History</v-subheader>
                  <v-divider />
                  <template v-if="audits && audits.length > 0">
                    <v-list-tile v-for="a in audits" :key="a.id">
                      <v-list-tile-content>
                        <v-list-tile-title>
                          <v-chip
                            :color="getActionColor(a.action)"
                            text-color="white"
                            small
                            class="mr-2"
                          >
                            {{ a.action }}
                          </v-chip>
                          {{ a.userName || "System" }}
                        </v-list-tile-title>
                        <v-list-tile-sub-title>
                          {{ formatAuditDate(a.timestamp) }}
                          <span
                            v-if="
                              a.changes && Object.keys(a.changes).length > 0
                            "
                            class="ml-2 font-weight-bold"
                          >
                            {{ getChangesPreview(a.changes) }}
                          </span>
                        </v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </template>
                  <v-list-tile v-else>
                    <v-list-tile-content>
                      <v-list-tile-title class="text-muted">
                        No changes recorded yet
                      </v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>
              </v-card>
            </div>
          </div>
        </div>

        <div class="matches-section">
          <kennected-result-list
            :athlete-id="$route.params.wrestler"
            :matches="filteredMatchesBySeason"
            :show-add-match-button="authenticated"
            :has-more="matchListHasMoreForUi"
            :loading-more="matchesLoadingMore"
            @add-match="showMatchDialog"
            @load-more="loadMoreMatches"
          >
            <template v-slot:header-actions>
              <v-select
                v-if="matchesForTimeline && matchesForTimeline.length > 0"
                v-model="selectedSeason"
                :items="seasonOptions"
                label="Season"
                hide-details
                dense
                dark
                class="season-filter-select season-filter-in-header"
              />
            </template>
          </kennected-result-list>
        </div>
      </div>

      <kennected-add-wrestler
        :dialog="showEditWrestler"
        :edit="edit"
        :wrestler="wrestler"
        :wrestler-id="$route.params.wrestler"
        @close-addWrestler="onEditWrestlerClose"
      />
      <kennected-add-match
        :dialog="addMatchDialog"
        :wrestler-name="wrestler.FullName"
        :wrestler-id="$route.params.wrestler"
        :wrestler-skill="wrestler.Skill"
        :wrestler-wins="displayWins"
        :wrestler-loses="displayLosses"
        :profile-wrestler="profileWrestlerForAddMatch"
        @close-addMatch="onAddMatchClose"
      />

      <div class="fab-wrap">
        <v-menu
          v-model="fabMenu"
          :close-on-content-click="false"
          offset-y
          transition="scale-transition"
          bottom
          right
          origin="bottom right"
          content-class="fab-menu"
        >
          <template v-slot:activator="{ on }">
            <v-btn class="fab-main" color="red" dark fab v-on="on">
              <v-icon>{{ fabMenu ? "close" : "menu" }}</v-icon>
            </v-btn>
          </template>
          <v-list class="fab-actions">
            <v-list-tile to="/" @click="fabMenu = false">
              <v-list-tile-avatar>
                <v-icon>home</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>Home</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile
              v-if="authenticated"
              @click="
                logOff();
                fabMenu = false;
              "
            >
              <v-list-tile-avatar>
                <v-icon>person_outline</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>Log out</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile v-else to="/login" @click="fabMenu = false">
              <v-list-tile-avatar>
                <v-icon>person</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>Log in</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile to="/user-guide" @click="fabMenu = false">
              <v-list-tile-avatar>
                <v-icon>menu_book</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>User Guide</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile
              @click="
                goBack();
                fabMenu = false;
              "
            >
              <v-list-tile-avatar>
                <v-icon>arrow_back</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>Back</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile
              v-if="authenticated"
              @click="
                showMatchDialog();
                fabMenu = false;
              "
            >
              <v-list-tile-avatar>
                <v-icon>add_circle</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>Add match</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile
              v-if="authenticated"
              @click="
                editWrestler();
                fabMenu = false;
              "
            >
              <v-list-tile-avatar>
                <v-icon>edit</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>Edit wrestler</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile
              v-if="authenticated"
              @click="
                triggerPhotoUpload();
                fabMenu = false;
              "
            >
              <v-list-tile-avatar>
                <v-icon>camera_alt</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>Change photo</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile
              @click="
                openCompareDialog();
                fabMenu = false;
              "
            >
              <v-list-tile-avatar>
                <v-icon>compare_arrows</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>Compare</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile
              @click="
                $root.toggleDarkMode && $root.toggleDarkMode();
                fabMenu = false;
              "
            >
              <v-list-tile-avatar>
                <v-icon>{{
                  $root.darkMode ? "brightness_5" : "brightness_3"
                }}</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{
                  $root.darkMode ? "Light mode" : "Dark mode"
                }}</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-menu>

        <v-dialog
          v-model="compareDialog"
          persistent
          :fullscreen="compareDialogMobile"
          :max-width="compareDialogMobile ? undefined : '520px'"
          content-class="compare-dialog"
          transition="dialog-transition"
        >
          <v-card class="compare-dialog-card">
            <div class="compare-dialog-header">
              <h2 class="compare-dialog-title">
                Compare with another wrestler
              </h2>
              <v-btn
                icon
                dark
                class="compare-dialog-close"
                aria-label="Close"
                @click="closeCompareDialog"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </div>
            <v-card-text class="compare-dialog-body">
              <v-text-field
                v-model="compareSearchQuery"
                label="Search wrestler"
                placeholder="Name, school, state, league, division, weight…"
                outlined
                dense
                hide-details
                clearable
                class="compare-search-input"
                autocomplete="off"
                @input="applyCompareSearch"
              >
                <template v-slot:prepend-inner>
                  <v-icon small class="compare-search-icon">search</v-icon>
                </template>
              </v-text-field>
              <div v-if="compareListLoading" class="compare-loading mt-3">
                <v-progress-circular indeterminate size="24" />
                <span>Loading wrestlers…</span>
              </div>
              <div
                v-else-if="
                  compareSearchQuery.trim() && compareSearchResults.length === 0
                "
                class="compare-no-results mt-3"
              >
                No wrestlers match. Try different words.
              </div>
              <div
                v-else-if="
                  compareSearchQuery.trim() && compareSearchResults.length > 0
                "
                class="compare-results-list mt-3"
              >
                <div
                  v-for="item in compareSearchResultsSlice"
                  :key="item.id"
                  class="compare-result-row"
                  @click="selectCompareWrestler(item)"
                >
                  <div class="compare-result-avatar">
                    <img
                      :src="
                        authenticated && item.data && item.data.Image
                          ? item.data.Image
                          : defaultCompareAvatar
                      "
                      alt=""
                      class="compare-result-avatar-img"
                      @error="$event.target.src = defaultCompareAvatar"
                    />
                  </div>
                  <div class="compare-result-content">
                    <span class="compare-result-name">{{ item.fullName }}</span>
                    <span class="compare-result-meta">
                      {{ compareResultMeta(item) }}
                    </span>
                  </div>
                </div>
                <p
                  v-if="compareSearchResults.length > COMPARE_RESULTS_LIMIT"
                  class="compare-results-more"
                >
                  {{ compareSearchResults.length - COMPARE_RESULTS_LIMIT }} more
                  — narrow your search
                </p>
              </div>
              <p
                v-else-if="!compareListLoading"
                class="compare-search-hint mt-2"
              >
                Type to search by name, school, state, league, division, or
                weight class.
              </p>
              <div
                v-if="compareLoading && compareSelected"
                class="compare-loading mt-3"
              >
                <v-progress-circular indeterminate size="24" />
                <span>Loading comparison…</span>
              </div>
              <template v-else-if="compareSelected && compareH2H">
                <div v-if="comparePrediction" class="compare-prediction mt-3">
                  <div class="compare-section-title">Match prediction</div>
                  <p class="compare-prediction-winner">
                    <strong>{{ comparePrediction.winnerName }}</strong> —
                    {{ comparePrediction.confidence }}% confidence
                  </p>
                  <p class="compare-prediction-summary">
                    {{ comparePrediction.summary }}
                  </p>
                </div>
                <div class="compare-h2h mt-3">
                  <div class="compare-section-title">Head-to-head</div>
                  <p class="compare-h2h-record">
                    {{ compareH2H.wins }}–{{ compareH2H.losses }} vs
                    {{ compareOtherName }}
                  </p>
                </div>
                <div
                  v-if="compareCommonOpponents.length"
                  class="compare-common mt-3"
                >
                  <div class="compare-section-title">Common opponents</div>
                  <div
                    v-for="(row, idx) in compareCommonOpponents"
                    :key="idx"
                    class="compare-common-row"
                  >
                    <span class="compare-common-name">{{ row.name }}</span>
                    <span class="compare-common-stats">
                      You: {{ row.myWins }}–{{ row.myLosses }} · Them:
                      {{ row.theirWins }}–{{ row.theirLosses }}
                    </span>
                  </div>
                </div>
                <p v-else class="compare-no-common mt-2">
                  No common opponents.
                </p>
              </template>
            </v-card-text>
            <v-card-actions class="compare-dialog-actions">
              <v-spacer />
              <v-btn
                class="compare-dialog-close-btn"
                @click="closeCompareDialog"
              >
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <input
          ref="photoInput"
          type="file"
          accept="image/*"
          class="hidden-input"
          @change="onPhotoSelected"
        />
      </div>
    </div>

    <div v-if="loading" class="wrestler-content">
      <div class="left-column">
        <skeleton-loader type="profile" class="skeleton-profile-card" />
        <skeleton-loader
          v-if="authenticated"
          type="card"
          class="skeleton-audit-card"
        />
      </div>
      <div class="matches-section">
        <skeleton-loader type="card" />
        <skeleton-loader type="card" />
        <skeleton-loader type="card" />
      </div>
    </div>
    <div v-if="!loading && !wrestler" class="loading-state">
      <v-icon size="48" color="grey">person_off</v-icon>
      <p>Wrestler not found.</p>
      <v-btn flat color="primary" to="/">Go home</v-btn>
    </div>
  </div>
</template>

<script>
import { getAuth, signOut } from "firebase/auth";
import { authState } from "../utils/authState";
import {
  getFirestore,
  doc,
  getDoc,
  getDocFromServer,
  updateDoc,
  collection,
  getDocs,
  query,
  orderBy,
  where,
  limit,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { store } from "../store";
import {
  fetchLeagueSkillMinMax,
  skillBarLinearFillPercent as linearSkillBarFillPercent,
  computeEffectiveSkillBarRange,
} from "../utils/skillBarLinear";
import { formatSkillLevel } from "../utils/formatSkillDisplay";
import { sortWrestlersByRankingRules } from "../utils/rankingSort";
import {
  normalizeSeasonToShort,
  currentSeasonLabelShort,
} from "../utils/seasonFilter";
import {
  fetchMergedCanonicalMatchRowsDesc,
  fetchAllPerspectiveRowsForAthlete,
} from "../utils/canonicalMatches";
import { computeMatchupContextAdjustment } from "../utils/compareMatchupAdjustment";

export default {
  components: {
    "kennected-result-list": () => import("../components/ResultList"),
    "kennected-add-match": () => import("../components/AddMatch"),
    "kennected-add-wrestler": () => import("./AddWrestler"),
    "skeleton-loader": () => import("../components/SkeletonLoader"),
  },
  data: () => ({
    wrestler: null,
    wrestlerMatches: [],
    /** Newest-first full list from merged winner/loser queries (client paging). */
    profileMatchesDesc: [],
    matchesDisplayCount: 20,
    /** Ascending by date — newest chunk (see matchTimelineLimit) for sparkline & seasons */
    matchesForTimeline: [],
    matchesHasMore: false,
    matchesLoadingMore: false,
    matchListPageSize: 20,
    matchTimelineLimit: 500,
    loading: true,
    fabMenu: false,
    addMatchDialog: false,
    file: null,
    showEditWrestler: false,
    edit: true,
    compareDialog: false,
    compareDialogMobile: false,
    compareSearchQuery: "",
    compareAllAthletes: [],
    compareLeagueMeta: {},
    compareWrestlerList: [],
    compareListLoading: false,
    compareSelected: null,
    compareLoading: false,
    compareH2H: null,
    compareOtherName: "",
    compareCommonOpponents: [],
    comparePrediction: null,
    COMPARE_RESULTS_LIMIT: 50,
    selectedSeason: "All",
    audits: [],
    weightRank: null,
    weightCount: null,
    previousRankInWeightClass: null,
    lastMatchSkillChange: null,
    defaultCompareAvatar: require("../assets/default-profile.png"),
    leagueSkillMin: null,
    leagueSkillMax: null,
  }),
  computed: {
    rankChangeFromPrevious() {
      if (this.weightRank == null || this.previousRankInWeightClass == null)
        return null;
      const diff = this.previousRankInWeightClass - this.weightRank;
      return diff === 0 ? null : diff;
    },
    seasonOptions() {
      const seasons = new Set();
      (this.matchesForTimeline || []).forEach((m) => {
        const s = m.data && m.data.Season;
        if (!s || !String(s).trim()) return;
        const n = normalizeSeasonToShort(String(s).trim());
        if (n && n !== "All") seasons.add(n);
      });
      return ["All", ...Array.from(seasons).sort().reverse()];
    },
    currentSeasonLabel() {
      return currentSeasonLabelShort();
    },
    seasonRecord() {
      const seasonKey = normalizeSeasonToShort(
        String(
          this.selectedSeason && this.selectedSeason !== "All"
            ? this.selectedSeason
            : this.currentSeasonLabel
        ).trim()
      );
      let wins = 0;
      let losses = 0;
      (this.matchesForTimeline || []).forEach((m) => {
        const s = m.data && m.data.Season ? String(m.data.Season).trim() : "";
        const docKey = normalizeSeasonToShort(s);
        if (!docKey || docKey !== seasonKey) return;
        if (m.data.Result === "Win") wins++;
        else if (m.data.Result === "Loss") losses++;
      });
      if (wins === 0 && losses === 0) return null;
      return { wins, losses };
    },
    filteredMatchesBySeason() {
      if (!this.selectedSeason || this.selectedSeason === "All") {
        return this.wrestlerMatches || [];
      }
      const timeline = this.matchesForTimeline || [];
      const sel = normalizeSeasonToShort(String(this.selectedSeason).trim());
      return timeline
        .filter((m) => {
          const doc =
            m.data && m.data.Season ? String(m.data.Season).trim() : "";
          return normalizeSeasonToShort(doc) === sel;
        })
        .slice()
        .reverse();
    },
    /** Infinite scroll only for “All”; a specific season uses the timeline slice (newest first). */
    matchListHasMoreForUi() {
      if (this.selectedSeason && this.selectedSeason !== "All") return false;
      return this.matchesHasMore;
    },
    displayWins() {
      const w = this.wrestler;
      const sw = w && typeof w.Wins === "number" ? w.Wins : 0;
      const sl = w && typeof w.Losses === "number" ? w.Losses : 0;
      if (sw !== 0 || sl !== 0) return sw;
      let dw = 0;
      for (const r of this.profileMatchesDesc || []) {
        if (r.data && r.data.Result === "Win") dw += 1;
      }
      return dw;
    },
    displayLosses() {
      const w = this.wrestler;
      const sw = w && typeof w.Wins === "number" ? w.Wins : 0;
      const sl = w && typeof w.Losses === "number" ? w.Losses : 0;
      if (sw !== 0 || sl !== 0) return sl;
      let dl = 0;
      for (const r of this.profileMatchesDesc || []) {
        if (r.data && r.data.Result === "Loss") dl += 1;
      }
      return dl;
    },
    profileWrestlerForAddMatch() {
      const w = this.wrestler;
      const id = this.$route.params.wrestler;
      if (!w || !id || id === "0") return null;
      const name =
        (w.FullName && String(w.FullName).trim()) ||
        [w.FirstName, w.LastName].filter(Boolean).join(" ").trim() ||
        "Wrestler";
      return {
        text: name,
        value: String(id),
        skill: w.Skill,
        wins: this.displayWins,
        losses: this.displayLosses,
        image: w.Image || "",
      };
    },
    skillBarLinearWidthPercent() {
      if (!this.wrestler || this.wrestler.Skill == null) return 0;
      const skill =
        typeof this.wrestler.Skill === "number"
          ? this.wrestler.Skill
          : Number(this.wrestler.Skill);
      const { low, high } = computeEffectiveSkillBarRange(
        this.leagueSkillMin,
        this.leagueSkillMax,
        skill
      );
      return linearSkillBarFillPercent(skill, high, low);
    },
    leagueSkillBarScaleHint() {
      if (
        this.leagueSkillMin != null &&
        this.leagueSkillMax != null &&
        Number.isFinite(this.leagueSkillMin) &&
        Number.isFinite(this.leagueSkillMax)
      ) {
        return (
          formatSkillLevel(this.leagueSkillMin) +
          "–" +
          formatSkillLevel(this.leagueSkillMax)
        );
      }
      return "league range unavailable";
    },
    skillTrendPoints() {
      const matches = this.matchesForTimeline || [];
      if (matches.length === 0) return [];
      const withDate = matches
        .filter((m) => m.data && m.data.MatchDate != null)
        .map((m) => {
          const d = m.data.MatchDate;
          const ts =
            typeof d.toDate === "function"
              ? d.toDate().getTime()
              : d && d.seconds
              ? d.seconds * 1000
              : new Date(d).getTime();
          return {
            ts,
            change:
              typeof m.data.SkillChange === "number" ? m.data.SkillChange : 0,
          };
        });
      if (withDate.length === 0) return [];
      withDate.sort((a, b) => a.ts - b.ts);
      const points = [];
      let skill = 1200;
      points.push({ ts: withDate[0].ts - 1, skill });
      withDate.forEach((m) => {
        skill += m.change;
        points.push({ ts: m.ts, skill });
      });
      const current = Number(this.wrestler && this.wrestler.Skill);
      if (typeof current === "number" && points.length > 0) {
        points.push({ ts: Date.now(), skill: current });
      }
      return points;
    },
    skillTrendPath() {
      const pts = this.skillTrendPoints;
      if (pts.length === 0) return "";
      if (pts.length === 1) return `M 0 14 L 120 14`;
      const skills = pts.map((p) => p.skill);
      const minS = Math.min(1200, ...skills);
      const maxS = Math.max(1200, ...skills);
      const range = maxS - minS || 1;
      const pad = range * 0.1;
      const lo = minS - pad;
      const hi = maxS + pad;
      const span = hi - lo || 1;
      const w = 120;
      const h = 28;
      const x = (i) => (i / (pts.length - 1)) * w;
      const y = (s) => h - ((s - lo) / span) * h;
      return pts
        .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p.skill)}`)
        .join(" ");
    },
    authenticated: function () {
      const auth = getAuth();
      return auth.currentUser != null;
    },
    compareSearchResults() {
      const q = (this.compareSearchQuery || "").trim().toLowerCase();
      if (!q) return [];
      const tokens = q.split(/\s+/).filter(Boolean);
      const list = this.compareAllAthletes || [];
      return list.filter((a) => {
        const name = (a.fullName || "").toLowerCase();
        const school = (a.school || "").toLowerCase();
        const state = (a.state || "").toLowerCase();
        const league = (a.league || "").toLowerCase();
        const division = (a.division || "").toLowerCase();
        const leagueName = (a.leagueName || "").toLowerCase();
        const weight = (a.weightClass || "").toLowerCase();
        const searchable = [
          name,
          school,
          state,
          league,
          division,
          leagueName,
          weight,
        ].join(" ");
        return tokens.every((t) => searchable.includes(t));
      });
    },
    compareSearchResultsSlice() {
      const limit = this.COMPARE_RESULTS_LIMIT || 50;
      return (this.compareSearchResults || []).slice(0, limit);
    },
  },
  watch: {
    $route: {
      handler: function (to, from) {
        if (to.name !== "wrestler" || !to.params.wrestler) return;
        // Refetch from server when returning from match page (so Wins/Losses and match list are correct after delete)
        if (from && from.name === "match") {
          this.getWrestler(true);
          return;
        }
        // Refetch when switching to a different wrestler
        if (from && from.params.wrestler !== to.params.wrestler) {
          this.getWrestler();
        }
      },
    },
  },
  mounted: function () {
    this.getWrestler();
    this.checkCompareDialogMobile();
    window.addEventListener("resize", this.checkCompareDialogMobile);
  },
  beforeDestroy: function () {
    window.removeEventListener("resize", this.checkCompareDialogMobile);
  },
  methods: {
    formatSkillLevel,
    triggerPhotoUpload: function () {
      this.$refs.photoInput && this.$refs.photoInput.click();
    },
    onPhotoSelected: function (e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.uploadFile(file);
      e.target.value = "";
    },
    uploadFile: function (e) {
      const storage = getStorage();
      const storageRef = ref(storage, "images/" + e.name);
      uploadBytes(storageRef, e)
        .then((response) => {
          getDownloadURL(response.ref).then((downloadURL) => {
            this.updateImage(downloadURL);
          });
        })
        .catch((err) => alert(err));
    },
    goBack: function () {
      this.$router.go(-1);
    },
    logOff: function () {
      const auth = getAuth();
      signOut(auth).then(
        () => {},
        () => {}
      );
    },
    showMatchDialog: function () {
      this.addMatchDialog = true;
    },
    onAddMatchClose: function () {
      this.addMatchDialog = false;
      const id = this.$route.params.wrestler;
      if (id) this.loadMatches(id);
    },
    editWrestler: function () {
      this.showEditWrestler = true;
    },
    onEditWrestlerClose: function () {
      this.showEditWrestler = false;
      const id = this.$route.params.wrestler;
      if (id) {
        this.getWrestler();
        if (this.authenticated) this.loadAudits(id);
      }
    },
    updateImage: function (downloadURL) {
      const db = getFirestore();
      const wrestlerRef = doc(db, "athletes", this.$route.params.wrestler);
      return updateDoc(wrestlerRef, { Image: downloadURL })
        .then(() => {
          this.wrestler = { ...this.wrestler, Image: downloadURL };
          this.$root.$emit("toast", "Photo updated.", "success");
        })
        .catch((error) => {
          const msg =
            error && error.message ? error.message : "Error updating image.";
          this.$root.$emit("toast", msg, "error");
        });
    },
    getWrestler: function (forceServer) {
      const athleteId = this.$route.params.wrestler;
      if (!athleteId) {
        this.wrestler = null;
        this.wrestlerMatches = [];
        this.profileMatchesDesc = [];
        this.matchesDisplayCount = this.matchListPageSize || 20;
        this.matchesForTimeline = [];
        this.matchesHasMore = false;
        this.audits = [];
        this.leagueSkillMin = null;
        this.leagueSkillMax = null;
        this.loading = false;
        return;
      }
      const db = getFirestore();
      const docRef = doc(db, "athletes", athleteId);
      const fetchDoc = forceServer ? getDocFromServer : getDoc;
      fetchDoc(docRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            this.wrestler = docSnap.data();
            this.loadLeagueSkillBoundsForProfile();
            this.loadMatches(athleteId);
            if (this.authenticated) this.loadAudits(athleteId);
          } else {
            this.wrestler = null;
            this.wrestlerMatches = [];
            this.profileMatchesDesc = [];
            this.matchesDisplayCount = this.matchListPageSize || 20;
            this.matchesForTimeline = [];
            this.matchesHasMore = false;
            this.audits = [];
            this.leagueSkillMin = null;
            this.leagueSkillMax = null;
          }
        })
        .catch(() => {
          this.wrestler = null;
          this.wrestlerMatches = [];
          this.profileMatchesDesc = [];
          this.matchesDisplayCount = this.matchListPageSize || 20;
          this.matchesForTimeline = [];
          this.matchesHasMore = false;
          this.audits = [];
          this.leagueSkillMin = null;
          this.leagueSkillMax = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async loadLeagueSkillBoundsForProfile() {
      this.leagueSkillMin = null;
      this.leagueSkillMax = null;
      const w = this.wrestler;
      const league =
        w && w.RankLeague != null ? String(w.RankLeague).trim() : "";
      if (!league) return;
      try {
        const db = getFirestore();
        const r = await fetchLeagueSkillMinMax(db, league);
        this.leagueSkillMin = r.min;
        this.leagueSkillMax = r.max;
      } catch (_e) {
        /* keep null */
      }
    },
    matchDateMillisFromMatch(val) {
      if (!val) return 0;
      try {
        if (typeof val.toDate === "function") return val.toDate().getTime();
        if (val instanceof Date) return val.getTime();
        return new Date(val).getTime();
      } catch (_e) {
        return 0;
      }
    },
    reconcileWinsLossesFromTimeline(athleteId) {
      const rows = this.profileMatchesDesc || [];
      const db = getFirestore();
      const winsFromMatches = rows.filter(
        (r) => r.data && r.data.Result === "Win"
      ).length;
      const lossesFromMatches = rows.filter(
        (r) => r.data && r.data.Result === "Loss"
      ).length;
      const storedWins =
        this.wrestler && this.wrestler.Wins != null ? this.wrestler.Wins : 0;
      const storedLosses =
        this.wrestler && this.wrestler.Losses != null
          ? this.wrestler.Losses
          : 0;
      if (
        winsFromMatches === storedWins &&
        lossesFromMatches === storedLosses
      ) {
        return;
      }
      const athleteRef = doc(db, "athletes", athleteId);
      updateDoc(athleteRef, {
        Wins: winsFromMatches,
        Losses: lossesFromMatches,
      })
        .then(() => {
          this.wrestler = {
            ...this.wrestler,
            Wins: winsFromMatches,
            Losses: lossesFromMatches,
          };
        })
        .catch(() => {});
    },
    loadMatchesTimeline(athleteId, mergedDesc) {
      const lim = this.matchTimelineLimit || 500;
      const rows = mergedDesc || [];
      this.matchesForTimeline = rows.slice(0, lim).slice().reverse();
      this.reconcileWinsLossesFromTimeline(athleteId);
    },
    applyMatchListDerivedState() {
      const rows = this.wrestlerMatches || [];
      if (
        rows.length > 0 &&
        rows[0].data &&
        typeof rows[0].data.SkillChange !== "undefined"
      ) {
        this.lastMatchSkillChange = rows[0].data.SkillChange;
      } else {
        this.lastMatchSkillChange = null;
      }
      this.loadWeightRank();
    },
    loadMoreMatches() {
      const athleteId = this.$route.params.wrestler;
      if (!athleteId || this.matchesLoadingMore || !this.matchesHasMore) return;
      if (this.selectedSeason && this.selectedSeason !== "All") return;
      this.matchesLoadingMore = true;
      try {
        const pageSize = this.matchListPageSize || 20;
        const all = this.profileMatchesDesc || [];
        this.matchesDisplayCount = Math.min(
          all.length,
          (this.matchesDisplayCount || pageSize) + pageSize
        );
        this.wrestlerMatches = all.slice(0, this.matchesDisplayCount);
        this.matchesHasMore = all.length > this.matchesDisplayCount;
        this.applyMatchListDerivedState();
      } finally {
        this.matchesLoadingMore = false;
      }
    },
    async fetchAthleteNameAndImage(athleteId) {
      const db = getFirestore();
      try {
        const s = await getDoc(doc(db, "athletes", athleteId));
        if (!s.exists()) return { name: "—", image: "" };
        const d = s.data() || {};
        const name =
          (d.FullName && String(d.FullName).trim()) ||
          [d.FirstName, d.LastName].filter(Boolean).join(" ").trim() ||
          "—";
        return { name, image: d.Image || "" };
      } catch (_e) {
        return { name: "—", image: "" };
      }
    },
    async loadMatches(athleteId) {
      const db = getFirestore();
      const pageSize = this.matchListPageSize || 20;
      if (!athleteId) {
        this.wrestlerMatches = [];
        this.profileMatchesDesc = [];
        this.matchesDisplayCount = pageSize;
        this.matchesForTimeline = [];
        this.matchesHasMore = false;
        return;
      }
      this.wrestlerMatches = [];
      this.profileMatchesDesc = [];
      this.matchesDisplayCount = pageSize;
      this.matchesForTimeline = [];
      this.matchesLoadingMore = false;
      try {
        const merged = await fetchMergedCanonicalMatchRowsDesc(
          db,
          athleteId,
          5000
        );
        this.profileMatchesDesc = merged;
        this.loadMatchesTimeline(athleteId, merged);
        this.matchesDisplayCount = Math.min(pageSize, merged.length);
        this.wrestlerMatches = merged.slice(0, this.matchesDisplayCount);
        this.matchesHasMore = merged.length > this.matchesDisplayCount;
        this.applyMatchListDerivedState();
      } catch (_e) {
        this.wrestlerMatches = [];
        this.profileMatchesDesc = [];
        this.matchesDisplayCount = pageSize;
        this.matchesForTimeline = [];
        this.matchesHasMore = false;
      }
    },

    async loadWeightRank() {
      try {
        if (
          !this.wrestler ||
          !this.wrestler.RankLeague ||
          (this.wrestler.RankWeightClass !== 0 &&
            !this.wrestler.RankWeightClass)
        ) {
          this.weightRank = null;
          this.weightCount = null;
          this.previousRankInWeightClass = null;
          return;
        }
        const db = getFirestore();
        const athletesRef = collection(db, "athletes");
        const league = String(this.wrestler.RankLeague || "").trim();
        const weightVal = this.wrestler.RankWeightClass;
        const weightNum = Number(weightVal);
        const tryQuery = (w) => {
          const q = query(
            athletesRef,
            where("RankLeague", "==", league),
            where("RankWeightClass", "==", w),
            orderBy("Skill", "desc")
          );
          return getDocs(q);
        };
        let snap = await tryQuery(weightVal).catch(() => null);
        if (!snap || snap.empty) {
          if (weightVal !== "" && !isNaN(weightNum)) {
            snap = await tryQuery(weightNum).catch(() => null);
          }
          if (!snap || snap.empty) {
            snap = await getDocs(
              query(
                athletesRef,
                where("RankLeague", "==", league),
                where("RankWeightClass", "==", weightVal)
              )
            ).catch(() => null);
          }
        }
        let list = [];
        if (snap && snap.forEach) {
          snap.forEach((d) => list.push({ id: d.id, data: d.data() }));
        }
        list = await sortWrestlersByRankingRules(db, list);
        this.weightCount = list.length;
        const idx = list.findIndex((r) => r.id === this.$route.params.wrestler);
        this.weightRank = idx >= 0 ? idx + 1 : null;

        this.previousRankInWeightClass = null;
        const athleteId = this.$route.params.wrestler;
        const weightStr = weightVal != null ? String(weightVal) : "";
        const snapshotId = `${league.replace(/\//g, "_")}__${weightStr.replace(
          /\//g,
          "_"
        )}`;
        try {
          const snapRef = doc(db, "rankingsSnapshots", snapshotId);
          const snapDoc = await getDoc(snapRef);
          if (snapDoc.exists() && snapDoc.data().ranks && athleteId) {
            const prev = snapDoc.data().ranks[athleteId];
            if (typeof prev === "number") this.previousRankInWeightClass = prev;
          }
        } catch (_) {}
      } catch (_e) {
        this.weightRank = null;
        this.weightCount = null;
        this.previousRankInWeightClass = null;
      }
    },
    loadAudits: function (athleteId) {
      if (!this.authenticated) {
        this.audits = [];
        return;
      }
      if (!athleteId) {
        this.audits = [];
        return;
      }
      const db = getFirestore();
      const auditsRef = collection(db, "auditLogs");
      const q = query(
        auditsRef,
        where("entityType", "==", "wrestler"),
        where("entityId", "==", athleteId)
      );
      getDocs(q)
        .then((snap) => {
          const rows = [];
          snap.forEach((docSnap) => {
            rows.push({ id: docSnap.id, ...docSnap.data() });
          });
          // Sort by timestamp in descending order (newest first)
          rows.sort((a, b) => {
            const timeA = a.timestamp
              ? typeof a.timestamp.toDate === "function"
                ? a.timestamp.toDate().getTime()
                : new Date(a.timestamp).getTime()
              : 0;
            const timeB = b.timestamp
              ? typeof b.timestamp.toDate === "function"
                ? b.timestamp.toDate().getTime()
                : new Date(b.timestamp).getTime()
              : 0;
            return timeB - timeA;
          });
          this.audits = rows;
        })
        .catch(() => {
          this.audits = [];
        });
    },
    openCompareDialog: function () {
      this.compareDialog = true;
      this.compareSelected = null;
      this.compareH2H = null;
      this.compareOtherName = "";
      this.compareCommonOpponents = [];
      this.comparePrediction = null;
      this.loadCompareWrestlerList();
    },
    checkCompareDialogMobile: function () {
      this.compareDialogMobile = window.innerWidth < 768;
    },
    closeCompareDialog: function () {
      this.compareDialog = false;
      this.compareSearchQuery = "";
      this.compareSelected = null;
      this.compareH2H = null;
      this.compareCommonOpponents = [];
      this.comparePrediction = null;
    },
    loadCompareWrestlerList: function () {
      const currentId = this.$route.params.wrestler;
      if (!currentId) return;
      this.compareListLoading = true;
      this.compareSearchQuery = "";
      this.compareAllAthletes = [];
      this.compareWrestlerList = [];
      const db = getFirestore();
      Promise.all([
        getDocs(query(collection(db, "leagues"), orderBy("Name"))),
        getDocs(query(collection(db, "athletes"), orderBy("FullName"))),
      ])
        .then(([leagueSnap, athleteSnap]) => {
          const meta = {};
          leagueSnap.forEach((d) => {
            const data = d.data() || {};
            const name =
              data.Name != null && String(data.Name).trim() !== ""
                ? String(data.Name).trim()
                : null;
            if (name) {
              meta[name] = {
                state:
                  data.State != null && String(data.State).trim() !== ""
                    ? String(data.State).trim()
                    : "",
                league:
                  data.League != null && String(data.League).trim() !== ""
                    ? String(data.League).trim()
                    : "",
                division:
                  data.Division != null && String(data.Division).trim() !== ""
                    ? String(data.Division).trim()
                    : "",
              };
            }
          });
          this.compareLeagueMeta = meta;
          const byId = new Map();
          athleteSnap.forEach((d) => {
            if (d.id === currentId) return;
            const data = d.data() || {};
            const fullName =
              data.FullName ||
              ((data.FirstName || "") + " " + (data.LastName || "")).trim() ||
              "—";
            const leagueName =
              data.RankLeague != null && String(data.RankLeague).trim() !== ""
                ? String(data.RankLeague).trim()
                : "";
            const info = meta[leagueName] || {
              state: "",
              league: "",
              division: "",
            };
            byId.set(d.id, {
              id: d.id,
              fullName,
              data,
              school:
                data.School != null && String(data.School).trim() !== ""
                  ? String(data.School).trim()
                  : "",
              state: info.state,
              league: info.league,
              division: info.division,
              leagueName,
              weightClass:
                data.RankWeightClass != null &&
                String(data.RankWeightClass).trim() !== ""
                  ? String(data.RankWeightClass).trim()
                  : "",
            });
          });
          this.compareAllAthletes = Array.from(byId.values());
          this.compareWrestlerList = this.compareAllAthletes.map((a) => ({
            id: a.id,
            fullName: a.fullName,
            data: a.data,
          }));
        })
        .catch(() => {
          this.compareAllAthletes = [];
          this.compareWrestlerList = [];
        })
        .finally(() => {
          this.compareListLoading = false;
        });
    },
    applyCompareSearch: function () {
      /* Filtering is done in computed compareSearchResults */
    },
    compareResultMeta: function (item) {
      const parts = [];
      if (item.school) parts.push(item.school);
      if (item.weightClass) parts.push(item.weightClass);
      if (item.leagueName) parts.push(item.leagueName);
      if (item.state && !parts.some((p) => p === item.state))
        parts.push(item.state);
      return parts.join(" · ") || "—";
    },
    selectCompareWrestler: function (item) {
      this.compareSelected = item.id;
      this.onCompareWrestlerSelected();
    },
    onCompareWrestlerSelected: function () {
      if (!this.compareSelected) {
        this.compareH2H = null;
        this.compareCommonOpponents = [];
        this.comparePrediction = null;
        return;
      }
      this.compareLoading = true;
      this.compareH2H = null;
      this.compareCommonOpponents = [];
      this.comparePrediction = null;
      const other = this.compareWrestlerList.find(
        (w) => w.id === this.compareSelected
      );
      this.compareOtherName = (other && other.fullName) || "—";
      const currentId = this.$route.params.wrestler;
      const otherId = this.compareSelected;
      const db = getFirestore();
      const loadMatches = (athleteId) =>
        fetchAllPerspectiveRowsForAthlete(db, athleteId);
      Promise.all([loadMatches(currentId), loadMatches(otherId)])
        .then(([myMatches, theirMatches]) => {
          let wins = 0;
          let losses = 0;
          myMatches.forEach((m) => {
            if (m.data.WrestlerID === otherId) {
              if (m.data.Result === "Win") wins++;
              else losses++;
            }
          });
          theirMatches.forEach((m) => {
            if (m.data.WrestlerID === currentId) {
              if (m.data.Result === "Loss") wins++;
              else losses++;
            }
          });
          this.compareH2H = { wins, losses };

          const myOpponents = new Map();
          myMatches.forEach((m) => {
            const oid = m.data.WrestlerID;
            if (!oid) return;
            if (!myOpponents.has(oid))
              myOpponents.set(oid, {
                name: m.data.OpponentFullName || "—",
                wins: 0,
                losses: 0,
              });
            const rec = myOpponents.get(oid);
            if (m.data.Result === "Win") rec.wins++;
            else rec.losses++;
          });
          const theirOpponents = new Map();
          theirMatches.forEach((m) => {
            const oid = m.data.WrestlerID;
            if (!oid) return;
            if (!theirOpponents.has(oid))
              theirOpponents.set(oid, {
                name: m.data.OpponentFullName || "—",
                wins: 0,
                losses: 0,
              });
            const rec = theirOpponents.get(oid);
            if (m.data.Result === "Win") rec.wins++;
            else rec.losses++;
          });
          const common = [];
          myOpponents.forEach((myRec, oid) => {
            if (oid === currentId || oid === otherId) return;
            const theirRec = theirOpponents.get(oid);
            if (!theirRec) return;
            common.push({
              name: myRec.name || theirRec.name || "—",
              myWins: myRec.wins,
              myLosses: myRec.losses,
              theirWins: theirRec.wins,
              theirLosses: theirRec.losses,
            });
          });
          this.compareCommonOpponents = common.slice(0, 15);
          this.comparePrediction = this.computeComparePrediction(
            other,
            wins,
            losses,
            common
          );
        })
        .catch(() => {
          this.compareH2H = { wins: 0, losses: 0 };
          this.compareCommonOpponents = [];
          this.comparePrediction = null;
        })
        .finally(() => {
          this.compareLoading = false;
        });
    },
    computeComparePrediction: function (
      otherWrestler,
      h2hWins,
      h2hLosses,
      commonOpponents
    ) {
      const myName =
        (this.wrestler &&
          (this.wrestler.FirstName + " " + this.wrestler.LastName).trim()) ||
        "You";
      const otherName = (otherWrestler && otherWrestler.fullName) || "Opponent";
      const mySkill = Number(this.wrestler && this.wrestler.Skill) || 200;
      const otherSkill =
        Number(
          otherWrestler && otherWrestler.data && otherWrestler.data.Skill
        ) || 200;

      const myRankLeague =
        this.wrestler &&
        this.wrestler.RankLeague != null &&
        String(this.wrestler.RankLeague).trim() !== ""
          ? String(this.wrestler.RankLeague).trim()
          : "";
      const otherData = otherWrestler && otherWrestler.data;
      const otherRankLeague =
        otherData &&
        otherData.RankLeague != null &&
        String(otherData.RankLeague).trim() !== ""
          ? String(otherData.RankLeague).trim()
          : "";
      const leagueMeta = this.compareLeagueMeta || {};
      const matchupAdj = computeMatchupContextAdjustment({
        myWeightClass: this.wrestler && this.wrestler.RankWeightClass,
        theirWeightClass: otherData && otherData.RankWeightClass,
        myLeagueMeta: myRankLeague ? leagueMeta[myRankLeague] || {} : {},
        theirLeagueMeta: otherRankLeague
          ? leagueMeta[otherRankLeague] || {}
          : {},
      });
      const contextNotes = [matchupAdj.weightDetail, matchupAdj.divisionDetail]
        .filter(Boolean);

      // ELO-style expected score: P(I win) = 1 / (1 + 10^((other - me)/400))
      // Matchup context shifts effective gap (heavier class / older division favors opponent).
      const skillGap =
        otherSkill - mySkill + (matchupAdj.eloShiftVsMe || 0);
      const eloDiff = skillGap / 400;
      let probMeWin = 1 / (1 + Math.pow(10, eloDiff));

      // Head-to-head: weight it more when there's a lot of history (so 22-6 actually flips the prediction)
      const totalH2H = h2hWins + h2hLosses;
      if (totalH2H > 0) {
        const h2hProbMeWin = h2hWins / totalH2H;
        const h2hWeight = Math.min(0.75, 0.35 + totalH2H * 0.015);
        const skillWeight = 1 - h2hWeight;
        probMeWin = skillWeight * probMeWin + h2hWeight * h2hProbMeWin;
      }

      // If we have common opponents, nudge by relative performance vs them
      if (commonOpponents && commonOpponents.length > 0) {
        let myWins = 0;
        let myLosses = 0;
        let theirWins = 0;
        let theirLosses = 0;
        commonOpponents.forEach((row) => {
          myWins += row.myWins || 0;
          myLosses += row.myLosses || 0;
          theirWins += row.theirWins || 0;
          theirLosses += row.theirLosses || 0;
        });
        const myTotal = myWins + myLosses;
        const theirTotal = theirWins + theirLosses;
        const myRate = myTotal > 0 ? myWins / myTotal : 0.5;
        const theirRate = theirTotal > 0 ? theirWins / theirTotal : 0.5;
        const commonFactor = myRate - theirRate;
        const nudge = commonFactor * 0.15;
        probMeWin = Math.max(0.05, Math.min(0.95, probMeWin + nudge));
      }

      const confidence = Math.round(
        probMeWin >= 0.5 ? probMeWin * 100 : (1 - probMeWin) * 100
      );
      const winnerName = probMeWin >= 0.5 ? myName : otherName;
      const parts = [];
      parts.push("Based on skill ratings");
      if (totalH2H > 0) parts.push("and head-to-head history");
      if (commonOpponents && commonOpponents.length > 0)
        parts.push(
          "and performance vs " + commonOpponents.length + " common opponent(s)"
        );
      const summary = parts.join(" ") + ".";

      return {
        winnerName,
        confidence,
        summary,
      };
    },
    formatAuditDate: function (d) {
      if (!d) return "";
      try {
        const val =
          typeof d.toDate === "function"
            ? d.toDate()
            : d instanceof Date
            ? d
            : new Date(d);
        return this.$moment(val).format("MMM D, YYYY h:mm a");
      } catch (_e) {
        return "";
      }
    },
    getActionColor: function (action) {
      switch (action) {
        case "create":
          return "#4CAF50";
        case "update":
          return "#FF9800";
        case "delete":
          return "#F44336";
        default:
          return "#9E9E9E";
      }
    },
    getChangesPreview: function (changes) {
      if (!changes || typeof changes !== "object") return "";
      const fields = Object.keys(changes).slice(0, 2);
      return fields.map((f) => `${f}`).join(", ");
    },
  },
};
</script>

<style scoped>
.wrestler-page {
  min-height: 100%;
  background: #f5f5f7;
  padding: 0.75rem;
  padding-bottom: 4rem;
}

.wrestler-content {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr;
}

.left-column {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

@media (min-width: 768px) {
  .wrestler-content {
    grid-template-columns: 340px 1fr;
    gap: 1rem;
    align-items: start;
  }
}

.profile-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.profile-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  gap: 0.75rem;
}

@media (min-width: 768px) {
  .profile-head {
    flex-direction: row;
    align-items: flex-start;
    padding: 1rem 1.25rem;
    gap: 1rem;
  }
}

.avatar-ring {
  flex-shrink: 0;
  padding: 4px;
  border-radius: 50%;
  background: linear-gradient(135deg, #c62828 0%, #b71c1c 100%);
  box-shadow: 0 2px 12px rgba(183, 28, 28, 0.2);
}

.profile-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  background: #fef2f2;
}

@media (min-width: 768px) {
  .profile-avatar {
    width: 88px;
    height: 88px;
  }
}

.profile-meta {
  flex: 1;
  min-width: 0;
  width: 100%;
  text-align: center;
}

@media (min-width: 768px) {
  .profile-meta {
    text-align: left;
  }
}

.wrestler-name {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

@media (min-width: 768px) {
  .wrestler-name {
    font-size: 1.35rem;
  }
}

.profile-stats {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}

@media (min-width: 768px) {
  .profile-stats {
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 0.5rem;
  }
}

.record-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.record-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.season-record {
  margin-left: 0;
}

@media (min-width: 768px) {
  .season-record {
    margin-left: 8px;
  }
}

.season-badge {
  font-size: 0.9rem;
}

.record-badge {
  font-size: 0.9rem;
  font-weight: 700;
  color: #b91c1c;
  background: #fef2f2;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
}

.skill-bar {
  min-width: 140px;
  max-width: 220px;
  position: relative;
}

.skill-bar-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.skill-info-icon {
  color: #6b7280;
  cursor: help;
  flex-shrink: 0;
}

.skill-info-icon:hover {
  color: #1f2937;
}

.skill-bar--linear .skill-bar-linear-track {
  height: 14px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
  position: relative;
}

.theme--dark .skill-bar--linear .skill-bar-linear-track {
  background: #374151;
}

.skill-bar--linear .skill-bar-linear-fill {
  height: 100%;
  border-radius: 999px;
  min-width: 0;
  background: linear-gradient(
    90deg,
    #22c55e 0%,
    #a3e635 22%,
    #facc15 48%,
    #f97316 74%,
    #b91c1c 100%
  );
  transition: width 0.25s ease;
}

.skill-bar-label {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.75rem;
  font-weight: 600;
  color: #111827;
  text-shadow: none;
  pointer-events: none;
}

.theme--dark .skill-bar-label {
  color: #f3f4f6;
}

.rank-in-weight-section {
  margin-top: 0.75rem;
  padding: 0.5rem 0;
  border-top: 1px solid #f3f4f6;
}

.rank-in-weight-section .detail-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 4px;
}

.rank-in-weight-value {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.rank-in-weight-text {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
}

.rank-in-weight-unset {
  font-size: 0.9rem;
  color: #9ca3af;
  font-style: italic;
}

.rank-change-pill {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
}

.rank-change-up {
  background: #dcfce7;
  color: #166534;
}

.rank-change-down {
  background: #fee2e2;
  color: #b91c1c;
}

.skill-sparkline-wrap {
  margin-top: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid #f3f4f6;
}

.skill-sparkline-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 4px;
}

.skill-sparkline {
  width: 100%;
  max-width: 200px;
  height: 28px;
  display: block;
  color: #b91c1c;
  opacity: 0.85;
}

@media (min-width: 768px) {
  .skill-sparkline {
    max-width: 240px;
  }
}

.detail-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.25rem 1rem;
  font-size: 0.8rem;
  text-align: left;
  max-width: 260px;
  margin: 0 auto;
}

@media (min-width: 768px) {
  .detail-grid {
    margin: 0;
    max-width: none;
  }
}

.detail-label {
  color: #6b7280;
}

.detail-value {
  color: #1f2937;
  font-weight: 500;
}

.matches-section {
  min-width: 0;
}

.season-filter-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.season-filter-label {
  font-size: 0.9rem;
  color: #6b7280;
  white-space: nowrap;
}

.season-filter-select {
  max-width: 140px;
}

@media (max-width: 767px) {
  .matches-section {
    margin-top: 0.5rem;
  }
}

.matches-section >>> .result-list-header {
  padding: 10px 12px;
  font-size: 0.9rem;
}

.matches-section >>> .result-row {
  padding: 10px 12px;
}

@media (max-width: 767px) {
  .matches-section >>> .result-row {
    min-height: 52px;
    padding: 14px 16px;
  }
}

@media (min-width: 768px) {
  .matches-section >>> .result-list-header {
    padding: 12px 16px;
  }
  .matches-section >>> .result-row {
    padding: 12px 16px;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 1rem;
  color: #6b7280;
  font-size: 0.95rem;
}

.fab-wrap {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 4;
}

@media (max-width: 767px) {
  .fab-wrap {
    bottom: max(1rem, env(safe-area-inset-bottom));
    right: max(1rem, env(safe-area-inset-right));
  }
}

.fab-main {
  box-shadow: 0 4px 14px rgba(183, 36, 36, 0.4) !important;
}

@media (max-width: 767px) {
  .fab-main {
    min-width: 52px !important;
    min-height: 52px !important;
    width: 56px !important;
    height: 56px !important;
  }
}

.fab-menu {
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  min-width: 200px;
}

.hidden-input {
  position: absolute;
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
}

/* Compare dialog – match Add Wrestler / Add Match / Edit Match look */
.compare-dialog-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.compare-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #c62828 0%, #b71c1c 100%);
  color: #fff;
}

.compare-dialog-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.compare-dialog-close {
  margin: 0;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.compare-dialog-close:hover {
  opacity: 1;
}

.compare-dialog-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.compare-search-input {
  width: 100%;
}

.compare-search-icon {
  opacity: 0.6;
}

.compare-search-hint {
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
}

.compare-no-results {
  font-size: 0.9rem;
  color: #6b7280;
}

.compare-results-list {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
}

.compare-result-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  transition: background 0.15s;
}

.compare-result-avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e7eb;
}

.compare-result-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.compare-result-content {
  min-width: 0;
  flex: 1;
}

.compare-result-row:last-child {
  border-bottom: none;
}

.compare-result-row:hover {
  background: #f0f4f8;
}

.compare-result-name {
  display: block;
  font-weight: 600;
  color: #1f2937;
  font-size: 0.95rem;
}

.compare-result-meta {
  display: block;
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 2px;
}

.compare-results-more {
  padding: 8px 12px;
  margin: 0;
  font-size: 0.8rem;
  color: #6b7280;
  border-top: 1px solid #eee;
}

.compare-dialog-actions {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.compare-dialog-close-btn {
  min-height: 40px;
  min-width: 100px;
  font-size: 0.95rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #374151;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 4px;
}

.compare-dialog-close-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

@media (max-width: 767px) {
  .compare-dialog-card {
    border-radius: 0;
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }

  .compare-dialog-header {
    padding: 0.75rem 1rem;
    flex-shrink: 0;
  }

  .compare-dialog-title {
    font-size: 1rem;
  }

  .compare-dialog-body {
    padding: 1rem;
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .compare-dialog-actions {
    padding: 0.75rem 1rem;
    flex-shrink: 0;
  }

  .compare-dialog-close-btn {
    width: 100%;
    min-height: 48px;
  }
}

.compare-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 0.9rem;
}

.compare-prediction {
  padding: 12px;
  background: #f0fdf4;
  border-radius: 8px;
  border-left: 4px solid #22c55e;
}

.compare-prediction-winner {
  margin: 0 0 4px;
  font-size: 1rem;
  color: #166534;
}

.compare-prediction-summary {
  margin: 0;
  font-size: 0.8rem;
  color: #6b7280;
}

.compare-prediction-context {
  margin: 8px 0 0;
  padding-left: 1.1rem;
  font-size: 0.78rem;
  color: #4b5563;
  line-height: 1.4;
}

.compare-prediction-context li {
  margin-bottom: 4px;
}

.compare-prediction-context li:last-child {
  margin-bottom: 0;
}

.compare-section-title {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
  font-size: 0.9rem;
}

.compare-h2h-record {
  margin: 0;
  font-size: 1rem;
  color: #374151;
}

.compare-common {
  border-top: 1px solid #e5e7eb;
  padding-top: 12px;
}

.compare-common-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.9rem;
  flex-wrap: wrap;
  gap: 4px;
}

.compare-common-name {
  font-weight: 500;
  color: #1f2937;
}

.compare-common-stats {
  color: #6b7280;
  font-size: 0.85rem;
}

.compare-no-common {
  margin: 0;
  color: #6b7280;
  font-size: 0.9rem;
}

/* Compare dialog – dark mode */
.theme--dark .compare-dialog-card {
  background: #1e1e1e;
}

.theme--dark .compare-dialog-actions {
  border-top-color: rgba(255, 255, 255, 0.12);
  background: #1e1e1e;
}

.theme--dark .compare-dialog-close-btn {
  color: #e5e7eb;
  border-color: #424242;
  background: #2d2d2d;
}

.theme--dark .compare-dialog-close-btn:hover {
  background: #383838;
  border-color: #4b4b4b;
}

.theme--dark .compare-loading {
  color: #9ca3af;
}

.theme--dark .compare-prediction {
  background: rgba(34, 197, 94, 0.15);
  border-left-color: #22c55e;
}

.theme--dark .compare-prediction-winner {
  color: #86efac;
}

.theme--dark .compare-prediction-summary {
  color: #9ca3af;
}

.theme--dark .compare-prediction-context {
  color: #9ca3af;
}

.theme--dark .compare-section-title {
  color: #e5e7eb;
}

.theme--dark .compare-h2h-record {
  color: #d1d5db;
}

.theme--dark .compare-common {
  border-top-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .compare-common-row {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .compare-common-name {
  color: #e5e7eb;
}

.theme--dark .compare-common-stats {
  color: #9ca3af;
}

.theme--dark .compare-no-common {
  color: #9ca3af;
}

.theme--dark .compare-search-hint,
.theme--dark .compare-no-results {
  color: #9ca3af;
}

.theme--dark .compare-results-list {
  border-color: rgba(255, 255, 255, 0.12);
  background: #252525;
}

.theme--dark .compare-result-row {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .compare-result-row:hover {
  background: #333;
}

.theme--dark .compare-result-avatar {
  background: #424242;
}

.theme--dark .compare-result-name {
  color: #e5e7eb;
}

.theme--dark .compare-result-meta {
  color: #9ca3af;
}

.theme--dark .compare-results-more {
  color: #9ca3af;
  border-top-color: rgba(255, 255, 255, 0.08);
}

/* Ensure Vuetify inputs/labels in compare dialog are readable in dark mode */
.theme--dark .compare-dialog-body .v-label,
.theme--dark .compare-dialog-body .v-input input,
.theme--dark .compare-dialog-body .v-input .v-select__selection {
  color: rgba(255, 255, 255, 0.87);
}

.theme--dark .compare-dialog-body .v-label {
  color: rgba(255, 255, 255, 0.6);
}

/* Mobile: profile card and touch targets */
@media (max-width: 767px) {
  .wrestler-page {
    padding: 0.5rem;
    padding-bottom: 4rem;
  }
  .profile-head {
    padding: 0.75rem;
    gap: 0.5rem;
  }
  .profile-card {
    border-radius: 10px;
  }
  .detail-grid {
    max-width: 100%;
  }
  .skill-bar-row {
    flex-wrap: wrap;
  }
  .fab-wrap .v-btn {
    min-width: 48px;
    min-height: 48px;
  }
}

/* Dark mode */
.theme--dark .wrestler-page {
  background: #121212;
}
.theme--dark .profile-card {
  background: #1e1e1e;
}
.theme--dark .profile-head {
  background: transparent;
}
.theme--dark .wrestler-name,
.theme--dark .detail-value,
.theme--dark .rank-in-weight-text {
  color: #e5e7eb;
}
.theme--dark .detail-label,
.theme--dark .record-label,
.theme--dark .skill-sparkline-label {
  color: #9ca3af;
}
.theme--dark .record-badge {
  background: rgba(185, 28, 28, 0.25);
  color: #fca5a5;
}
.theme--dark .rank-in-weight-section {
  border-top-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .skill-sparkline-wrap {
  border-top-color: #374151;
}
.theme--dark .skill-sparkline {
  color: #f87171;
}

.theme--dark .season-filter-label {
  color: #9ca3af;
}
</style>
